import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store';

var API_BASE = '/api';

function getHeaders() {
  var token = useAuthStore.getState().token;
  return { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
}

export function DirectionMigrationPanel() {
  var _s = useState(null), preview = _s[0], setPreview = _s[1];
  var _s2 = useState(null), applyResult = _s2[0], setApplyResult = _s2[1];
  var _s3 = useState(false), loading = _s3[0], setLoading = _s3[1];

  var handlePreview = async function() {
    setLoading(true);
    try {
      var res = await fetch(API_BASE + '/intercompany/migrate-direction/preview', { headers: getHeaders() });
      var data = await res.json();
      setPreview(data);
      setApplyResult(null);
    } catch (err) {
      toast.error('Preview failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  var handleApply = async function() {
    if (!window.confirm('Apply direction migration? This swaps from/to on transaction-derived entries.')) return;
    setLoading(true);
    try {
      var res = await fetch(API_BASE + '/intercompany/migrate-direction', { method: 'POST', headers: getHeaders() });
      var data = await res.json();
      setApplyResult(data);
      toast.success('Migration ' + data.status + ': ' + data.migrated + ' entries');
    } catch (err) {
      toast.error('Apply failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Intercompany Direction Migration</CardTitle>
        <CardDescription>Swap from/to on transaction-derived entries to match debt-direction semantics</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handlePreview} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Preview
          </Button>
          <Button size="sm" onClick={handleApply} disabled={loading || !preview || preview.status === 'already_applied'}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Apply
          </Button>
        </div>

        {preview && preview.status === 'already_applied' && (
          <div className="text-sm">
            <Badge variant="outline" className="text-green-600">Already applied</Badge>
            <span className="ml-2 text-muted-foreground">No entries need migration</span>
          </div>
        )}

        {preview && preview.status === 'pending' && (
          <div className="text-sm space-y-2">
            <div>
              <Badge variant="secondary">{preview.count} entries</Badge>
              <span className="ml-2 text-muted-foreground">to migrate</span>
            </div>
            {preview.entries && preview.entries.length > 0 && (
              <div className="overflow-auto max-h-64">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-1">ID</th>
                      <th className="text-left p-1">From (before)</th>
                      <th className="text-left p-1">To (before)</th>
                      <th className="text-left p-1">From (after)</th>
                      <th className="text-left p-1">To (after)</th>
                      <th className="text-right p-1">Amount</th>
                      <th className="text-left p-1">Ccy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.entries.map(function(e) {
                      return (
                        <tr key={e.id} className="border-b">
                          <td className="p-1 font-mono">{e.id}</td>
                          <td className="p-1">{e.from_entity}</td>
                          <td className="p-1">{e.to_entity}</td>
                          <td className="p-1 text-green-600">{e.from_entity_after}</td>
                          <td className="p-1 text-green-600">{e.to_entity_after}</td>
                          <td className="p-1 text-right">{e.amount}</td>
                          <td className="p-1">{e.currency}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="text-sm">
            <Badge variant={applyResult.status === 'applied' ? 'default' : 'outline'}>
              {applyResult.status}
            </Badge>
            <span className="ml-2 text-muted-foreground">
              {applyResult.migrated} entries migrated
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
